import { Graphics } from "pixi.js";
import { cellToPixel, PIXELS_PER_CELL } from "../../views/shop-scale";
import { Persistence } from "../../config/constants";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { GameSprite } from "../../core/entity/GameSprite";
import { on } from "../../core/entity/handler";
import {
  BASE_WALK_SPEED,
  cellCenter,
  CollisionWorld,
  directionFromInput,
  headingForDirection,
  SolidCircle,
  stepPlayerMotion,
} from "../../game/player-motion";
import {
  cabStandCell,
  corralStandCell,
  findShelf,
  registerStandCell,
  ShelfBay,
  storeCollisionWorld,
  StoreLayout,
  storeLayout,
} from "../../game/store-layout";
import {
  cartIndexToReturn,
  resolveStoreInteract,
  StoreInteract,
} from "../../game/store-interact";
import { Direction, Vector, vectorKey } from "../../game/Vectors";
import {
  addToCart,
  checkout,
  removeFromCart,
  takeCart,
} from "../../sim/commands/cart-commands";
import { setShoppingPosition } from "../../sim/commands/trip-commands";
import { Player } from "../../sim/entities/Player";

import { FootstepSoundView, WalkingBody } from "../../views/FootstepSoundView";
import { Game } from "../../core/Game";
import { ShellStore } from "../ShellStore";
import { SceneDirector } from "./SceneDirector";
import { TRIP_TRANSITIONS_DISABLED, TripTheater } from "./TripTheater";
import { StoreActorsView } from "./store-views/StoreActorsView";
import { StoreDaylightView } from "./store-views/StoreDaylightView";
import {
  SHOPPER_RADIUS,
  shopperPatrols,
  StoreShopper,
} from "./store-views/StoreShopper";
import { StoreEnvironmentView } from "./store-views/StoreEnvironmentView";
import {
  STORE_DEPART_SECONDS,
  StoreTruckView,
} from "./store-views/StoreTruckView";
import { StoreFixturesView } from "./store-views/StoreFixturesView";
import { ShopVacEntity } from "../../sim/entities/ShopVacEntity";
import { Broom } from "../../sim/singletons/Broom";
import { Wallet } from "../../sim/singletons/Wallet";
import { Reputation } from "../../sim/singletons/Reputation";
import { StoreMerchandiseView } from "./store-views/StoreMerchandiseView";

/**
 * The walkable Orange Box's scene root (migration phase 6). Spawned by
 * the SceneDirector while a shopping trip to the store is on screen;
 * Level persistence, so any scene clear takes it (the director rebuilds
 * what the venue needs).
 *
 * The scene-swap milestone draws the venue's bones — slab, walls with
 * their two door gaps, gondola spines and fixture blocks, register and
 * corral — and owns the store floor's walk: the same continuous body
 * math as the shop (`stepPlayerMotion`) over the planogram's collision
 * world, at the clean-floor stride (no sawdust in a store), reporting
 * the cell underfoot onto the trip (`away.position` — `player.position`
 * keeps meaning the cell back home). The full fixture/merchandise/
 * shopper dress arrives with the StoreScene work; the camera runs both
 * axes here, following the body.
 */

export class StoreSceneRoot extends BaseEntity implements Entity {
  id = "storeSceneRoot";
  persistenceLevel: number = Persistence.Level;
  pausable = false;

  /** The continuous store body, in cell coordinates. */
  private position: Vector | null = null;
  private direction: Direction = 3;
  /** The last motion's continuous heading, for the cart's lead point. */
  private heading = Math.PI / 2;
  /** Whether input carried the body anywhere this tick, for the steps. */
  private moving = false;
  private lastReported = "";

  /** The ambient crowd's entities (this root's children), kept in a
   * list so the walk treats them as solid, walking people rather than
   * scenery. */
  private crowd: StoreShopper[] = [];
  /** The floor's collision world, rebuilt each tick for the walk — the
   * crowd's entities read it back through walkWorld(). */
  private floorWorld: CollisionWorld | null = null;

  /** The floor's collision world, for whoever walks it (ShopperVenue). */
  walkWorld(): CollisionWorld | null {
    return this.floorWorld;
  }

  /** The continuous body, for the views riding it. */
  bodyPosition(): Vector | null {
    return this.position;
  }

  bodyHeading(): number {
    return this.heading;
  }

  /** Whether the body is being walked right now — what the steps hear. */
  bodyMoving(): boolean {
    return this.moving;
  }

  private layoutCache: StoreLayout | null = null;
  /** What the cached layout was built for — the same slices the old
   * StoreView's memo keyed on (the floor only moves when stock moves). */
  private layoutKey = "";

  // ---- The store floor's transient UI state (the old StoreView's) ----

  /** The register's receipt card. */
  checkoutOpen = false;
  /** The armed "leave the cart behind?" at the cab. */
  armedLeave = false;
  /** Real seconds left before the armed confirm disarms itself. */
  private armedLeaveLeft = 0;

  /** How long an armed "leave the cart behind?" waits before disarming. */
  private static readonly CONFIRM_TIMEOUT_S = 5;

  /**
   * Seconds since the register rang the cart up, or null while the
   * truck just sits in its stall. Once it's running, the shopper is in
   * the cab and the truck is the player on screen: the body and its
   * flatbed step off the floor, nothing on the floor answers a key, and
   * the head-home fade comes up under the roll.
   */
  private departSeconds: number | null = null;
  /** Whether the fade home has been asked for yet. */
  private headedHome = false;

  /** How far into the pull-out the truck is, in seconds. */
  departElapsed(): number | null {
    return this.departSeconds;
  }

  /** When the head-home fade starts within the truck's pull-out. */
  private static readonly DEPART_FADE_LEAD_S = 0.5;

  constructor() {
    super();
    this.sprites = [];
  }

  layout(): StoreLayout | null {
    const player = this.game.entities.tryGetSingleton(Player);
    if (player?.away?.kind !== "shopping") return null;
    const stock = {
      reputation: this.game.entities.getSingleton(Reputation).reputation,
      broomOwned: this.game.entities.tryGetSingleton(Broom)?.owned ?? false,
      shopVac: this.game.entities.tryGetSingleton(ShopVacEntity) ?? null,
    };
    const key = [
      player.away.store,
      stock.reputation,
      stock.broomOwned,
      stock.shopVac != null,
    ].join(",");
    if (!this.layoutCache || key !== this.layoutKey) {
      this.layoutCache = storeLayout(player.away.store, stock);
      this.layoutKey = key;
      if (this.isAdded) this.dress(this.layoutCache);
      publishStoreHooks(this.layoutCache);
    }
    return this.layoutCache;
  }

  /** The drawn layers this root put up, torn down on each redress —
   * the children that aren't drawings (the footsteps) stay put. */
  private dressing: Entity[] = [];

  /** (Re)build the venue's drawn layers for a layout — on add, and
   * whenever the planogram moves (stock changes relayout the floor). */
  private dress(layout: StoreLayout): void {
    while (this.dressing.length) {
      this.dressing.pop()?.destroy();
    }
    this.dressing = [
      this.addChild(new StoreEnvironmentView(layout)),
      this.addChild(new StoreFixturesView(layout)),
      this.addChild(new StoreMerchandiseView(layout)),
      this.addChild(new StoreActorsView()),
      this.addChild(new StoreTruckView(layout)),
      this.addChild(new StoreDaylightView(layout)),
    ];
  }

  /** The resolver the keys and the chips share (store-interact.ts). */
  interact(): StoreInteract | null {
    if (this.departSeconds !== null) return null;
    const layout = this.layout();
    if (!layout) return null;
    return resolveStoreInteract(
      {
        money: this.game.entities.getSingleton(Wallet).money,
        player: this.game.entities.getSingleton(Player),
      },
      layout,
    );
  }

  // ---- The store floor's keys (dispatched by ShortcutDispatcher) ----

  /** E: take a cart, take one off the shelf, ring up, or head home. */
  pressE(): void {
    const now = this.interact();
    if (!now) return;
    if (now.atRegister) {
      if (now.canCheckOut) this.openCheckout();
      return;
    }
    if (now.atCab) {
      this.leave();
      return;
    }
    if (now.atCorral && !now.hasCart) {
      takeCart(this.game);
      this.bump();
      return;
    }
    if (now.fixture && now.hasCart) {
      this.addFromBay(now.fixture);
    }
  }

  /** F: put one of the standing bay's product back on its shelf. */
  pressF(): void {
    const now = this.interact();
    if (now?.fixture && now.inCart > 0) {
      this.returnToBay(now.fixture);
    }
  }

  /** Escape: disarm the "leave the cart?" confirm. True when the press
   * was spent. (The receipt card is a real modal — its own modal scope
   * owns Escape while it's up, like any other dialog.) */
  pressEscape(): boolean {
    if (this.armedLeave) {
      this.armedLeave = false;
      this.bump();
      return true;
    }
    return false;
  }

  addFromBay(bay: ShelfBay): void {
    addToCart(this.game, bay.product.line);
    this.bump();
  }

  returnToBay(bay: ShelfBay): void {
    const index = cartIndexToReturn(
      { player: this.game.entities.getSingleton(Player) },
      bay,
    );
    if (index !== null) {
      removeFromCart(this.game, index);
      this.bump();
    }
  }

  openCheckout(): void {
    this.checkoutOpen = true;
    this.bump();
  }

  closeCheckout(): void {
    this.checkoutOpen = false;
    this.bump();
  }

  /**
   * The receipt's Buy: ring the cart up — the goods land in the truck's
   * bed — then the truck pulls out of the stall and the screen heads
   * home under it.
   */
  buy(): void {
    if (!checkout(this.game)) return;
    this.checkoutOpen = false;
    this.bump();
    if (TRIP_TRANSITIONS_DISABLED) {
      this.headHome();
      return;
    }
    this.departSeconds = 0;
    this.headedHome = false;
  }

  /**
   * Dip to black over the store, then charge the drive: the destination
   * is what fades out, not the shop (TripTheater).
   */
  private headHome(): void {
    const director = this.game.entities.getSingleton(SceneDirector);
    const theater = this.game.entities.tryGetSingleton(TripTheater);
    if (theater) {
      theater.headHome(() => director.requestDriveHome());
    } else {
      director.requestDriveHome();
    }
  }

  /**
   * The cab's E. A loaded cart asks first (the armed confirm, which
   * disarms on a timeout, on stepping away, or when the cart empties);
   * pressing again — or standing there cartless — heads home.
   */
  leave(): void {
    const now = this.interact();
    if ((now?.cartCount ?? 0) > 0 && !this.armedLeave) {
      this.armedLeave = true;
      this.armedLeaveLeft = StoreSceneRoot.CONFIRM_TIMEOUT_S;
      this.bump();
      return;
    }
    this.armedLeave = false;
    this.bump();
    this.headHome();
  }

  private bump(): void {
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("add")
  onAdd() {
    // The shopper's own steps, on the store's floor. Added outside
    // `dress` so a relayout doesn't reset the walker's cadence, and
    // after this root so each tick's sample sees the step just taken.
    this.addChild(new FootstepSoundView(walkingShopper, Persistence.Level));
    const layout = this.layout();
    if (layout) this.dress(layout);
  }

  @on("destroy")
  onDestroy() {
    publishStoreHooks(null);
  }

  @on("tick")
  onTick(dt: number) {
    const player = this.game.entities.tryGetSingleton(Player);
    const layout = this.layout();
    if (!player || player.away?.kind !== "shopping" || !layout) {
      this.moving = false;
      return;
    }
    const trip = player.away;

    // The truck pulling out of the stall: the roll runs, and the fade
    // home comes up under it half a second before the roll ends, so the
    // screen goes black over a truck that is already moving. The body
    // is in the cab by then, so the floor stops answering the keys
    // (below) while the rest of the store carries on around it.
    const departing = this.departSeconds !== null;
    if (this.departSeconds !== null) {
      this.departSeconds += dt;
      if (
        !this.headedHome &&
        this.departSeconds >=
          STORE_DEPART_SECONDS - StoreSceneRoot.DEPART_FADE_LEAD_S
      ) {
        this.headedHome = true;
        this.headHome();
      }
    }

    // The armed "leave the cart behind?" disarms on its timeout, on
    // stepping away from the cab, or when the cart empties.
    if (this.armedLeave) {
      this.armedLeaveLeft -= dt;
      const now = this.interact();
      if (
        this.armedLeaveLeft <= 0 ||
        !now?.atCab ||
        (now?.cartCount ?? 0) === 0
      ) {
        this.armedLeave = false;
        this.bump();
      }
    }

    // Mount snap: the body stands wherever the trip does — the spawn
    // beside the cab on arrival, mid-aisle on a reload.
    if (this.position === null) {
      this.position = cellCenter(trip.position);
      this.direction = trip.direction;
      this.heading = headingForDirection(trip.direction);
    }

    // The ambient crowd: one StoreShopper entity per walker, spawned as
    // this root's children — they stroll their patrols on their own
    // ticks, reading the body and this world back through ShopperVenue.
    if (this.crowd.length === 0) {
      this.crowd = shopperPatrols(layout).map((patrol) =>
        this.addChild(new StoreShopper(this, patrol)),
      );
    }
    const baseWorld = storeCollisionWorld(layout);
    this.floorWorld = baseWorld;

    // A DOM dialog owns the keyboard here exactly like at home.
    const modalOpen =
      this.game.entities.tryGetSingleton(ShellStore)?.modalOpen ?? false;
    const input =
      modalOpen || departing
        ? ([0, 0] as Vector)
        : this.game.io.getMovementVector();
    this.moving = input[0] !== 0 || input[1] !== 0;
    if (this.moving) {
      this.direction = directionFromInput(input, this.direction);
      this.heading = Math.atan2(input[1], input[0]);
      // The shoppers are solid, walking people, not scenery the body
      // clips through.
      const shopperSolids: SolidCircle[] = this.crowd.map((shopper) => ({
        kind: "circle",
        center: shopper.position,
        radius: SHOPPER_RADIUS,
      }));
      const world: CollisionWorld = {
        ...baseWorld,
        solids: [...baseWorld.solids, ...shopperSolids],
      };
      this.position = stepPlayerMotion(
        this.position,
        input,
        BASE_WALK_SPEED,
        dt,
        world,
      );
    }

    // Report the cell underfoot onto the trip when it changes.
    const cell: Vector = [
      Math.floor(this.position[0]),
      Math.floor(this.position[1]),
    ];
    const key = `${vectorKey(cell)},${this.direction}`;
    if (key !== this.lastReported) {
      this.lastReported = key;
      setShoppingPosition(this.game, cell, this.direction);
    }
  }

  @on("render")
  onRender() {
    if (!this.position) return;
    const renderer = this.game.renderer;
    if (!renderer) return;

    // The camera pans both axes at the shop's own zoom, following the
    // body, clamped a stride past the walls and the lot's far edge (the
    // old CameraLayer's scroll ranges); a viewport big enough to see
    // everything collapses a range and that axis never moves.
    const layout = this.layoutCache;
    const camera = this.game.camera;
    if (layout) {
      const halfW = renderer.getWidth() / 2 / camera.z;
      const halfH = renderer.getHeight() / 2 / camera.z;
      const slack = cellToPixel(1.5);
      const clamp = (value: number, lo: number, hi: number) =>
        hi <= lo ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, value));
      camera.x = clamp(
        cellToPixel(this.position[0]),
        -slack + halfW,
        cellToPixel(layout.worldSize[0]) + slack - halfW,
      );
      camera.y = clamp(
        cellToPixel(this.position[1]),
        -slack + halfH,
        cellToPixel(layout.worldSize[1]) + slack - halfH,
      );
    } else {
      camera.x = cellToPixel(this.position[0]);
      camera.y = cellToPixel(this.position[1]);
    }
  }
}

/** The shopper walking the sales floor, for the footsteps to sample. */
function walkingShopper(game: Game): WalkingBody | null {
  const scene = game.entities.tryGetSingleton(StoreSceneRoot);
  const position = scene?.bodyPosition();
  if (!scene || !position) return null;
  return { position: [position[0], position[1]], moving: scene.bodyMoving() };
}

/**
 * The store floor's test surface, the same three names the old shell
 * published: the planogram, the cells worth standing on, and a shelf
 * looked up by the name on its tag. A spec walks the floor the player
 * walks, so this is how it finds where to stand. Cleared when the trip
 * ends, so nothing points at a store that isn't there.
 */
function publishStoreHooks(layout: StoreLayout | null): void {
  if (process.env.NODE_ENV === "production") return;
  const hooks = globalThis as unknown as Record<string, unknown>;
  if (!layout) {
    delete hooks.__STORE_LAYOUT__;
    delete hooks.__STORE_POINTS__;
    delete hooks.__FIND_SHELF__;
    return;
  }
  hooks.__STORE_LAYOUT__ = layout;
  hooks.__STORE_POINTS__ = {
    register: registerStandCell(layout),
    cab: cabStandCell(layout),
    corral: corralStandCell(layout),
    spawn: layout.spawn.cell,
  };
  hooks.__FIND_SHELF__ = (product: string) => findShelf(layout, product);
}
